import { Http } from "@angular/http";
import {
    QUERY_PARAMS,
    SERVICE_URLS,
    ITCIApplicationMonitoringProvider,
    ITCIApplication,
    ITCIMetrics,
    ITCIMetricsStatus,
    ITCIMetricsLabel,
    TCIMetricsLabelType,
    TCITenantAppType,
    ITCIMonitoringInstance
} from "@tci/common";
import { Injectable, NgZone } from "@angular/core";
import { TciTenantModelService } from "../../../../../ng-tenant/tci-tenant.model";
import { TciTenantService } from "../../../../../ng-tenant/tci-tenant.service";
import { NgEnvVarsService } from "../../../../../ng-shared/ng-services/ng-env-vars.service";
import { TCITenantMenuServices } from "../../../../../ng-tenant/tci-tenant.menu";


import { Observable, Subject, BehaviorSubject } from "rxjs";
import * as lodash from "lodash";
import * as moment from "moment";

export interface ITestCpuMemoryData {
    cpu: {
        name: number,
        value: number
    };
    memory: {
        name: number,
        value: number
    };
}

export interface ITCIResourceData {
    name: string;
    series: { name: string, value: number }[];
}

export interface ITCICPUMemoryResourceData {
    cpu: ITCIResourceData;
    memory: ITCIResourceData;
}

@Injectable()
export class BaseSubmenuMonitoringResourceMetricsService {
    private app: ITCIApplication;
    private instanceId: ITCIMonitoringInstance;
    private rangeId: number;

    private labels = { cpu: "CPU", memory: "Memory", success: "Success", failure: "Failure" };

    private cpuStats$: BehaviorSubject<ITCIMetricsStatus[]> = new BehaviorSubject([]);
    private memoryStats$: BehaviorSubject<ITCIMetricsStatus[]> = new BehaviorSubject([]);

    private cpuChart$: BehaviorSubject<ITCIResourceData[]> = new BehaviorSubject([
        { name: this.labels.cpu, series: [] }
    ]);
    private memoryChart$: BehaviorSubject<ITCIResourceData[]> = new BehaviorSubject([
        { name: this.labels.memory, series: [] }
    ]);
    private execSuccChart$: BehaviorSubject<ITCIResourceData[]> = new BehaviorSubject([
        { name: this.labels.success, series: [] }
    ]);
    private execFailChart$: BehaviorSubject<ITCIResourceData[]> = new BehaviorSubject([
        { name: this.labels.failure, series: [] }
    ]);

    constructor(
        private http: Http,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private envVarsSvc: NgEnvVarsService,
        private menuService: TCITenantMenuServices,
        private ngZone: NgZone,
    ) { }

    getNgZone() {
        return this.ngZone;
    }

    getCPUStatsSubject() {
        return this.cpuStats$;
    }

    getMemoryStatsSubject() {
        return this.memoryStats$;
    }

    getCpuChartSubject() {
        return this.cpuChart$;
    }

    getMemoryChartSubject() {
        return this.memoryChart$;
    }

    getExecSuccChartSubject() {
        return this.execSuccChart$;
    }

    getExecFailChartSubject() {
        return this.execFailChart$;
    }

    initializeData(app: ITCIApplication, instanceId: ITCIMonitoringInstance, rangeId: number) {
        this.app = app;
        this.instanceId = instanceId;
        this.rangeId = rangeId;
        this.getAppMetrics()
            .subscribe(res => {
                if (!lodash.isEmpty(res.labeledAppMetrics)) {
                    // For stats
                    this.sendStatsData(res.labeledAppMetrics);

                    // For charts
                    this.sendChartsData(res.labeledAppMetrics);
                }
            });
    }

    // For initial data feed
    private getAppMetrics(): Observable<ITCIMetrics> {
        const isMigrationReady = this.menuService.isMigrationReadytag(this.app.metaTags);

        const url = `${SERVICE_URLS.METRICS_RECORDS}/${this.app.id}?` +
            `${QUERY_PARAMS.APP_TYPE}=${this.app.type.toString()}&` +
            `${QUERY_PARAMS.INSTANCE_ID}=${this.instanceId.ip || this.instanceId.key}&` + // this.instanceId.ip is populated only in CIC2
            `${QUERY_PARAMS.METRICS_CATEGORY}=appDetail&` +
            `${QUERY_PARAMS.METRICS_IS_MIGRATION_READY}=${isMigrationReady}&` +
            `${QUERY_PARAMS.METRICS_RANGE_ID}=${this.rangeId}&onPremise=${this.app.onPremise}`;
        return this.http.get(url)
            .map(res => res.json());
    }

    private sendStatsData(data: ITCIMetricsLabel) {
        const checkValues = (vldata: ITCIMetricsStatus[]) => {
            ["max", "min", "avg"].forEach(status => {
                const value = vldata.find(d => d.labels.status === status);
                if (!value) {
                    vldata.push({ labels: { app_id: this.app.id, instance_id: this.instanceId.key, status: status as any }, value: 0 });
                }
            });

            return vldata;
        };

        const instCpuData = (data[TCIMetricsLabelType.tci_app_instances_cpu] || [])
            .filter(d => d.labels.app_id === this.app.id)
            .filter(d => this.instanceId.key.startsWith(d.labels.instance_id));
        this.cpuStats$.next(checkValues(instCpuData));

        const instMemoryData = (data[TCIMetricsLabelType.tci_app_instances_memory] || [])
            .filter(d => d.labels.app_id === this.app.id)
            .filter(d => this.instanceId.key.startsWith(d.labels.instance_id));
        this.memoryStats$.next(checkValues(instMemoryData));
    }

    private sendChartsData(data: ITCIMetricsLabel) {
        const prepareData = (rangeData: ITCIMetricsStatus[], name: string): ITCIResourceData => {
            const instData = (rangeData || [])
                .filter(d => d.labels.app_id === this.app.id)
                .filter(d => this.instanceId.key.startsWith(d.labels.instance_id));

            const valuesData: ITCIResourceData = { name, series: [] };
            instData.forEach(d => {
                d.values.forEach(v => {
                    valuesData.series.push({ name: moment.unix(v[0]).format(), value: lodash.round(v[1], 2) });
                });
            });

            // Test code
            // const date = moment();
            // for (let i = 0; i < 61; i++) {
            //     date.add(1, "s");
            //     const dateFormat = date.format();
            //     valuesData.series.push({ name: dateFormat, value: lodash.random(0, 100) });
            // }

            valuesData.series = lodash.sortBy(valuesData.series, "name");

            if (lodash.isEmpty(valuesData.series) || lodash.sum(valuesData.series.map(s => s.value)) === 0) {
                const date = moment().subtract(30, "m");
                for (let i = 0; i < 29; i++) {
                    date.add(1, "m");
                    const dateFormat = date.format();
                    valuesData.series.push({ name: dateFormat, value: 0 });
                }
            }
            return valuesData;
        };

        const cpuData = prepareData(data[TCIMetricsLabelType.tci_app_instances_cpu_range], this.labels.cpu);
        const memoryData = prepareData(data[TCIMetricsLabelType.tci_app_instances_memory_range], this.labels.memory);
        this.cpuChart$.next([cpuData]);
        this.memoryChart$.next([memoryData]);

        const successData = prepareData(data[TCIMetricsLabelType.tci_app_instances_success_exes_range], this.labels.success);
        const failureData = prepareData(data[TCIMetricsLabelType.tci_app_instances_failure_exes_range], this.labels.failure);
        this.execSuccChart$.next([successData]);
        this.execFailChart$.next([failureData]);
    }

    getTenantMonitorProvider(app: ITCIApplication): Observable<ITCIApplicationMonitoringProvider> {
        return this.tenantSvc.onInit().map(() => {
            const tenant = this.tenantModelSvc.getTenant(app.type);
            if (tenant) {
                const provider = tenant.getMonitoringServiceProvider();
                if (provider) {
                    return provider;
                } else {
                    const errMsg = "No monitoring provider for this application type: " + app.type.toString();
                    Observable.throw(errMsg);
                }
            }
        });
    }

    getMonitoringService(tenantType: TCITenantAppType): Observable<ITCIApplicationMonitoringProvider> {
        return this.tenantSvc.onInit().map(res => {
            const tenant = this.tenantModelSvc.getTenant(tenantType);
            const errMsg = "No monitoring provider for this application type: " + tenantType.toString();
            if (tenant) {
                const provider = tenant.getMonitoringServiceProvider();
                if (provider) {
                    return provider;
                } else {
                    console.warn(errMsg);
                    throw new Error(errMsg);
                }
            } else {
                console.warn(errMsg);
                throw new Error(errMsg);
            }
        });
    }

    // IPAS-13413 - No executions for Node apps
    canShowExecutionsSection(app: ITCIApplication): boolean {
        return !TCITenantAppType.node.equals(app.type);
    }
}
